/**
 * Decider（驗證決定委員）專用佈局 — 對齊 ZHCB design tokens（v3）
 * 白色側邊欄，寬 230px，Logo Sky→Teal 漸變
 * 標題「決定委員工作臺」，頂欄含組織搜尋框
 *
 * 檔案保留 CounselorLayout 名稱以避免破壞現有 import，
 * 但導出的組件已更名為 DeciderLayout。
 */
import { useMemo } from 'react';
import { Input } from 'antd';
import { LayoutDashboard, Landmark, ShieldCheck, Scale, FileCheck2, Search } from 'lucide-react';
import BaseLayout from '@/components/layout/BaseLayout';
import { getMenuForWorkbench } from '@/components/layout/menu-registry';
import type { BaseLayoutConfig, MenuItem } from '@/components/layout/layout-menu-config';
import { useAuthStore } from '@/lib/auth-context';
import { color, radius, roleAccent, text } from '@/styles/tokens';

/**
 * Decider 菜單 icon 映射表
 */
const DECIDER_ICON_MAP: Record<string, React.ReactNode> = {
  '/dashboard': <LayoutDashboard size={16} />,
  '/bulletin': <Landmark size={16} />,
  '/certified-orgs': <Landmark size={16} />,
  'audit-zone': <ShieldCheck size={16} />,
  '/decisions': <Scale size={16} />,
  '/certificates': <FileCheck2 size={16} />,
};

/** 為 Decider 菜單項注入 icon */
function injectDeciderIcons(items: MenuItem[]): MenuItem[] {
  return items.map((item) => {
    if (!item || !('key' in item)) return item;
    const icon = DECIDER_ICON_MAP[item.key as string];
    return icon ? { ...item, icon } : item;
  });
}

/** 頂欄左側插槽：組織搜尋框 */
function DeciderHeaderExtra() {
  return (
    <Input
      placeholder="搜尋組織名稱或驗證案件…"
      prefix={<Search size={14} style={{ color: color.text.muted }} />}
      style={{
        width: 320,
        borderRadius: radius.md,
        background: color.surface.muted,
        fontSize: text.md,
      }}
      allowClear
    />
  );
}

interface DeciderLayoutProps {
  children: React.ReactNode;
}

/**
 * Decider（驗證決定委員）專用佈局。
 * 以驗證決定為核心，Sky→Teal 漸變品牌色，白色側邊欄。
 *
 * Task 9：菜單來源改為 getMenuForWorkbench('decider', user, user.menuHidden ?? [])，
 * 以真實用戶物件動態過濾（Decider 樹全恆亮，過濾後結果與靜態版本相同）。
 */
export default function DeciderLayout({ children }: DeciderLayoutProps) {
  /* 讀取真實用戶物件，供菜單動態過濾使用（Decider 樹全恆亮，過濾後等同靜態菜單） */
  const user = useAuthStore((s) => s.user);

  const config: BaseLayoutConfig = useMemo(
    () => ({
      sider: {
        width: 230,
        background: color.surface.base,
        borderRight: `1px solid ${color.border.subtle}`,
      },
      logo: {
        title: '決定委員工作臺',
        titleColor: color.text.primary,
        gradient: roleAccent.counselor.gradient,
      },
      menuItems: injectDeciderIcons(
        getMenuForWorkbench(
          'decider',
          user ? { capabilities: user.capabilities, roles: user.roles } : {},
          user?.menuHidden ?? [],
        ),
      ),
      headerLeftExtra: <DeciderHeaderExtra />,
    }),
    [user],
  );

  return <BaseLayout config={config}>{children}</BaseLayout>;
}

/** 向後相容別名（原 CounselorLayout import 的備用） */
export { DeciderLayout as CounselorLayout };
